if (!customElements.get('wishlist-form')) {
  customElements.define(
    'wishlist-form',
    class extends HTMLElement {
      constructor() {
        super();
        this.apiBaseUrl = 'https://mackintosh-backend.vercel.app';
        this.initialize();
      }

      async initialize() {
        const customerId = window.customer && window.customer.id ? window.customer.id : null;
        let uuid = this.getWishlistUuid();

        if (customerId && uuid && uuid !== customerId) {
          await this.migrateWishlistToCustomer(customerId, uuid);
          uuid = customerId;
          this.setCookie('wishlist_uuid', uuid);
        }

        if (!uuid) {
          uuid = crypto.randomUUID();
          this.setCookie('wishlist_uuid', uuid);
        }

        this.loadCache();
        await this.authenticate(uuid);
        this.loadWishlist();
        this.setupEventListeners();
      }

      /**
       * Migrate the wishlist to the customer
       * @param {string} customerId - The customer ID
       * @param {string} oldUuid - The old UUID
       */
      async migrateWishlistToCustomer(customerId, oldUuid) {
        await this.authenticate(oldUuid);
        await this.fetchWishlistContents();
        const itemsToMigrate = this.cachedWishlistItems || [];

        await this.authenticate(customerId);
        await this.fetchAndSetupLists();

        for (const item of itemsToMigrate) {
          await this.addToWishlist(item.empi, item.epi, item.du);
        }
      }

      setupEventListeners() {
        window.addEventListener('modal-trap-activate', (e) => {
          if (e.detail.id === 'cart-tray') {
            this.loadWishlist();
          }
        });

        document.addEventListener('wishlist:toggle', async (/** @type {CustomEvent} */ event) => {
          const productData = event.detail.productData;
          const isInWishlist = await this.isProductInWishlist(productData);
          if (isInWishlist) {
            await this.removeFromWishlist(productData.empi, productData.epi, productData.du);

            window.gtag('event', 'wishlist_action', {
              product_url: productData.du,
              interaction_type: 'remove',
              element_type: 'toggle',
              page_path_clean: window.location.pathname.replace(/\/$/, ''),
            });
          } else {
            await this.addToWishlist(productData.empi, productData.epi, productData.du);

            window.gtag('event', 'wishlist_action', {
              product_url: productData.du,
              interaction_type: 'add',
              element_type: 'toggle',
              page_path_clean: window.location.pathname.replace(/\/$/, ''),
            });
          }
        });

        const shareButton = document.querySelector('.wishlist-share');
        if (shareButton) {
          shareButton.addEventListener('click', () => this.shareWishlist());
        }
      }

      async shareWishlist() {
        if (!this.regid || !this.sessionId || !this.listId) {
          let uuid = this.getWishlistUuid();

          if (!uuid) {
            uuid = crypto.randomUUID();
            this.setCookie('wishlist_uuid', uuid);
          }
          await this.authenticate(uuid);
        }

        /** @type {HTMLButtonElement | null} */
        const shareButton = document.querySelector('.wishlist-share');

        if (!shareButton) {
          return;
        }

        shareButton.setAttribute('data-loading', 'true');
        const originalText = shareButton.textContent;

        try {
          const response = await fetch(`${this.apiBaseUrl}/api/lists/share`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              regid: this.regid,
              sessionid: this.sessionId,
              lid: this.listId,
            }),
          });

          if (!response.ok) throw new Error('Failed to share wishlist');

          window.gtag('event', 'wishlist_action', {
            interaction_type: 'click',
            element_type: 'share',
            page_path_clean: window.location.pathname.replace(/\/$/, ''),
          });

          const data = await response.json();

          const shareUrl = `${window.location.origin}/pages/shared-wishlist?id=${data.lid}`;
          await navigator.clipboard.writeText(shareUrl);

          shareButton.textContent = shareButton.dataset.wishlistCopiedText || 'Link copied to clipboard';

          setTimeout(() => {
            shareButton.textContent = originalText;
            shareButton.removeAttribute('data-loading');
          }, 3000);
        } catch {
          shareButton.textContent = originalText;
          shareButton.removeAttribute('data-loading');
        }
      }

      /**
       * Check if a product is in the wishlist
       * @param {object} productData - The product data to check
       * @param {string} productData.epi - The product ID
       * @param {string} productData.empi - The variant ID
       * @returns {Promise<boolean>} True if the product is in the wishlist
       */
      async isProductInWishlist(productData) {
        if (!this.cachedWishlistItems) {
          await this.fetchWishlistContents();
        }

        return (
          this.cachedWishlistItems?.some(
            (/** @type {import('./types').WishlistItem} */ item) =>
              item.epi === parseInt(productData.epi) && item.empi === parseInt(productData.empi),
          ) || false
        );
      }

      async loadWishlist() {
        if (this.cachedWishlistItems) {
          this.renderWishlistItems(this.cachedWishlistItems);
        }
        await this.fetchWishlistContents();
      }

      async fetchWishlistContents() {
        if (!this.regid || !this.sessionId || !this.listId) {
          return;
        }

        try {
          const response = await fetch(`${this.apiBaseUrl}/api/lists/content`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              regid: this.regid,
              sessionid: this.sessionId,
              lid: this.listId,
            }),
          });

          if (!response.ok) {
            throw new Error('Failed to fetch wishlist contents');
          }

          const data = await response.json();

          this.cachedWishlistItems = data.items;
          this.saveCache();
          this.renderWishlistItems(data.items);

          const countIndicator = document.querySelector('[data-wishlist-item-count]');
          if (countIndicator) {
            countIndicator.textContent = this.cachedWishlistItems.length;
          }
        } catch {
          if (this.cachedWishlistItems) {
            this.renderWishlistItems(this.cachedWishlistItems);
          }
        }
      }

      getWishlistUuid() {
        return this.getCookie('wishlist_uuid');
      }

      /**
       * Get a cookie value by name
       * @param {string} name - The name of the cookie to get
       * @returns {string|null} The cookie value or null if not found
       */
      getCookie(name) {
        const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
        return match ? decodeURIComponent(match[2]) : null;
      }

      /**
       * Set a cookie with a value and expiration
       * @param {string} name - The name of the cookie to set
       * @param {string} value - The value to set
       * @param {number} days - Number of days until the cookie expires
       */
      setCookie(name, value, days = 365) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        document.cookie = `${name}=${encodeURIComponent(value)};expires=${date.toUTCString()};path=/`;
      }

      /**
       * Authenticate with the wishlist service
       * @param {string} uuid - The unique identifier for user
       */
      async authenticate(uuid) {
        try {
          if (!uuid || !uuid.match(/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i)) {
            uuid = crypto.randomUUID();
            this.setCookie('wishlist_uuid', uuid);
          }

          const response = await fetch(`${this.apiBaseUrl}/api/auth`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ uuid }),
          });

          if (!response.ok) {
            throw new Error('Authentication failed');
          }

          const data = await response.json();
          this.regid = data.regid;
          this.sessionId = data.sessionid;

          this.setCookie('wishlist_regid', this.regid);
          this.setCookie('wishlist_sessionid', this.sessionId);

          this.dispatchEvent(
            new CustomEvent('wishlist:authenticated', {
              bubbles: true,
              detail: {
                regid: this.regid,
                sessionId: this.sessionId,
                listId: this.listId,
              },
            }),
          );

          await this.fetchAndSetupLists();
        } catch {
          // Failed to authenticate
        }
      }

      async fetchAndSetupLists() {
        try {
          const response = await fetch(`${this.apiBaseUrl}/api/lists/all`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              regid: this.regid,
              sessionid: this.sessionId,
            }),
          });

          if (!response.ok) {
            throw new Error('Failed to fetch lists');
          }

          const lists = await response.json();

          if (!lists || lists.length === 0) {
            await this.createDefaultList();
          } else {
            this.listId = lists[0].lid;
            this.setCookie('wishlist_lid', this.listId);
          }
        } catch {
          // Failed to fetch lists
        }
      }

      async createDefaultList() {
        try {
          const response = await fetch(`${this.apiBaseUrl}/api/lists/create`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              lname: 'Default List',
              regid: this.regid,
              sessionid: this.sessionId,
            }),
          });

          if (!response.ok) {
            throw new Error('Failed to create default list');
          }

          const data = await response.json();

          this.listId = data.lid;
          this.setCookie('wishlist_lid', this.listId);

          this.dispatchEvent(
            new CustomEvent('wishlist:list-created', {
              bubbles: true,
              detail: { listId: this.listId },
            }),
          );
        } catch {
          // Failed to create default list
        }
      }

      /**
       * Add a product to the wishlist
       * @param {number} productId - The product ID to add
       * @param {number} variantId - The variant ID to add
       * @param {string} url - The product URL
       */
      async addToWishlist(productId, variantId, url) {
        if (!this.regid || !this.sessionId || !this.listId) {
          return;
        }

        try {
          const response = await fetch(`${this.apiBaseUrl}/api/lists/update`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              regid: this.regid,
              sessionid: this.sessionId,
              lid: this.listId,
              a: JSON.stringify([
                {
                  epi: variantId,
                  empi: productId,
                  du: url,
                },
              ]),
            }),
          });

          if (response.ok) {
            this.cachedWishlistItems = null;
            await this.fetchWishlistContents();

            document.dispatchEvent(
              new CustomEvent('wishlist:updated', {
                bubbles: true,
                detail: { items: this.cachedWishlistItems },
              }),
            );
          } else {
            throw new Error('Failed to add item to wishlist');
          }
        } catch {
          // Failed to add item to wishlist
        }
      }

      /**
       * Remove a product from the wishlist
       * @param {number} productId - The product ID to remove
       * @param {number} variantId - The variant ID to remove
       * @param {string} url - The product URL
       */
      async removeFromWishlist(productId, variantId, url) {
        if (!this.regid || !this.sessionId || !this.listId) {
          return;
        }

        const wishlistItem = document.querySelector(
          `[data-wishlist-item][data-product='{"epi":${variantId},"empi":${productId},"du":"${url}"}']`,
        );

        if (wishlistItem) {
          wishlistItem.setAttribute('data-loading', 'true');
        }

        try {
          const response = await fetch(`${this.apiBaseUrl}/api/lists/update`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              regid: this.regid,
              sessionid: this.sessionId,
              lid: this.listId,
              d: JSON.stringify([
                {
                  epi: variantId,
                  empi: productId,
                  du: url,
                },
              ]),
            }),
          });

          if (response.ok) {
            this.cachedWishlistItems = null;
            await this.fetchWishlistContents();

            document.dispatchEvent(
              new CustomEvent('wishlist:updated', {
                bubbles: true,
                detail: { items: this.cachedWishlistItems },
              }),
            );
          } else {
            throw new Error('Failed to remove item from wishlist');
          }
        } catch {
          // Failed to remove item from wishlist
        }
      }

      /**
       * Render the wishlist items in the DOM
       * @param {Array<{epi: number, empi: number, du: string, dt: string, vi: string, pr: number, iu: string}>} items - The wishlist items to render
       */
      renderWishlistItems(items) {
        /** @type {HTMLDivElement | null} */
        const container = this.querySelector('.wishlist-items');

        if (!container) return;

        const emptyText = container.dataset.emptyText || 'Your wishlist is empty';

        container.innerHTML = `
        <ul class="cart-items">
        ${items.length === 0 ? `<li class="cart-item">${emptyText}</li>` : ''}
        ${items
          .map((item) => {
            const url = new URL(item.du);
            url.protocol = window.location.protocol;
            url.host = window.location.host;
            url.search = '';
            const updatedUrl = url.toString();

            return `
           <li class="cart-item" data-wishlist-item data-product='{"epi":${item.epi},"empi":${item.empi},"du":"${updatedUrl}"}'>
            <div class="cart-item-content">
            <div class="cart-item-header">
              <h3 class="text-title-md">
                <a href="${updatedUrl}">${item.dt}</a>
              </h3>
            </div>
            <div class="cart-item-price text-subtitle-sm">
              £${item.pr.toFixed(0)}
            </div>
            <div class="cart-item-controls text-subtitle-sm">
              <button class="link-button text-subtitle-sm" data-product='{
                "epi": ${item.epi},
                "empi": ${item.empi},
                "du": "${updatedUrl}"
              }'
              data-wishlist-remove>
                Remove
              </button>
            </div>
          </div>
          <div class="cart-item-media">
            <a href="${updatedUrl}">
              <img src="${item.iu}" alt="${item.dt}" width="320" loading="lazy">
            </a>
          </div>
        </li>
        `;
          })
          .join('')}
        </ul>
        `;

        container.querySelectorAll('[data-wishlist-remove]').forEach((button) => {
          if (button instanceof HTMLButtonElement) {
            button.addEventListener('click', () => {
              const productData = JSON.parse(button.dataset.product || '{}');
              this.removeFromWishlist(productData.empi, productData.epi, productData.du);

              window.gtag('event', 'wishlist_action', {
                product_url: productData.du,
                interaction_type: 'remove',
                element_type: 'tray',
                page_path_clean: window.location.pathname.replace(/\/$/, ''),
              });
            });
          }
        });
      }

      saveCache() {
        localStorage.setItem('wishlist_cache', JSON.stringify(this.cachedWishlistItems));
      }

      loadCache() {
        const cached = localStorage.getItem('wishlist_cache');
        if (cached) {
          this.cachedWishlistItems = JSON.parse(cached);
        }
      }
    },
  );
}
